import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface PartnerSearchListing {
  id: string;
  event_id: string;
  user_id: string;
  category: string | null;
  subcategory: string | null;
  preferred_position: 'drive' | 'reves';
  status: string;
  stripe_payment_intent_id: string | null;
  matched_with: string | null;
  registration_id: string | null;
  created_at: string;
  updated_at: string;
  profile?: {
    full_name: string | null;
    avatar_url: string | null;
    skill_level: string | null;
  };
}

export interface WaitlistPair {
  id: string;
  drive: PartnerSearchListing | null;
  reves: PartnerSearchListing | null;
}

interface UsePartnerSearchOptions {
  eventId: string;
  category?: string | null;
  subcategory?: string | null;
}

function buildWaitlists(listings: PartnerSearchListing[]): WaitlistPair[] {
  const sorted = [...listings].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  const drives = sorted.filter(l => l.preferred_position === 'drive');
  const reves = sorted.filter(l => l.preferred_position === 'reves');

  const pairs: WaitlistPair[] = [];
  const maxLen = Math.max(drives.length, reves.length);

  for (let i = 0; i < maxLen; i++) {
    const d = drives[i] || null;
    const r = reves[i] || null;
    pairs.push({
      id: d?.id || r?.id || `pair-${i}`,
      drive: d,
      reves: r,
    });
  }

  return pairs;
}

export const usePartnerSearch = ({ eventId, category, subcategory }: UsePartnerSearchOptions) => {
  const { user } = useAuth();
  const [listings, setListings] = useState<PartnerSearchListing[]>([]);
  const [globalUserListing, setGlobalUserListing] = useState<PartnerSearchListing | null>(null);
  const [inactiveUserListing, setInactiveUserListing] = useState<PartnerSearchListing | null>(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const channelRef = useRef<string>(`partner-search-${eventId}-${Math.random().toString(36).slice(2)}`);

  const fetchListings = useCallback(async () => {
    try {
      // Main query: filtered by category/subcategory, only searching
      const query = supabase
        .from('partner_search_listings')
        .select('*')
        .eq('event_id', eventId)
        .eq('status', 'searching');

      if (category) query.eq('category', category);
      if (subcategory) query.eq('subcategory', subcategory);

      const { data, error } = await query;
      if (error) throw error;

      // Secondary queries for user state
      let userGlobalListing: PartnerSearchListing | null = null;
      let userInactiveListing: PartnerSearchListing | null = null;

      if (user) {
        // Find user's active listing for this event (any category)
        const { data: userActiveData } = await supabase
          .from('partner_search_listings')
          .select('*')
          .eq('event_id', eventId)
          .eq('user_id', user.id)
          .eq('status', 'searching')
          .maybeSingle();

        if (userActiveData) {
          const { data: userProfile } = await supabase
            .from('profiles')
            .select('user_id, full_name, avatar_url, skill_level')
            .eq('user_id', user.id)
            .maybeSingle();

          userGlobalListing = {
            ...userActiveData,
            preferred_position: userActiveData.preferred_position as 'drive' | 'reves',
            profile: userProfile || undefined,
          };
        } else {
          // Check for inactive listing (expired/cancelled) for this event
          const { data: userInactiveData } = await supabase
            .from('partner_search_listings')
            .select('*')
            .eq('event_id', eventId)
            .eq('user_id', user.id)
            .in('status', ['expired', 'cancelled'])
            .order('updated_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (userInactiveData) {
            userInactiveListing = {
              ...userInactiveData,
              preferred_position: userInactiveData.preferred_position as 'drive' | 'reves',
            };
          }
        }

        setGlobalUserListing(userGlobalListing);
        setInactiveUserListing(userInactiveListing);
      }

      if (data && data.length > 0) {
        const userIds = data.map(l => l.user_id);
        const { data: profiles } = await supabase
          .from('profiles')
          .select('user_id, full_name, avatar_url, skill_level')
          .in('user_id', userIds);

        const profileMap = new Map(profiles?.map(p => [p.user_id, p]) || []);
        const enriched = data.map(l => ({
          ...l,
          preferred_position: l.preferred_position as 'drive' | 'reves',
          profile: profileMap.get(l.user_id) || undefined,
        }));
        setListings(enriched);
      } else {
        setListings([]);
      }
    } catch (err) {
      console.error('[usePartnerSearch] Error fetching listings:', err);
    } finally {
      setLoading(false);
    }
  }, [eventId, category, subcategory, user]);

  useEffect(() => {
    fetchListings();

    const channel = supabase
      .channel(channelRef.current)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'partner_search_listings',
        filter: `event_id=eq.${eventId}`,
      }, () => {
        fetchListings();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchListings, eventId]);

  const filteredUserListing = listings.find(l => l.user_id === user?.id);
  const userListing = filteredUserListing || globalUserListing || undefined;
  const isUserInDifferentCategory = !filteredUserListing && !!globalUserListing;

  const waitlists = useMemo(() => buildWaitlists(listings), [listings]);

  const joinSearch = async (position: 'drive' | 'reves') => {
    if (!user) return;
    setJoining(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-partner-search-payment', {
        body: {
          eventId,
          category: category || null,
          subcategory: subcategory || null,
          preferredPosition: position,
        }
      });

      if (error) throw error;

      if (data?.clientSecret) {
        await fetchListings();
        return data;
      } else if (data?.listingId) {
        await fetchListings();
        return data;
      }
    } catch (err) {
      console.error('[usePartnerSearch] Error joining search:', err);
      throw err;
    } finally {
      setJoining(false);
    }
  };

  const cancelSearch = async () => {
    const listingToCancel = filteredUserListing || globalUserListing;
    if (!user || !listingToCancel) return;
    try {
      const { data, error } = await supabase.functions.invoke('cancel-partner-search', {
        body: { listingId: listingToCancel.id },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      await fetchListings();
    } catch (err) {
      console.error('[usePartnerSearch] Error cancelling search:', err);
      throw err;
    }
  };

  return {
    listings,
    loading,
    joining,
    userListing,
    isUserInDifferentCategory,
    inactiveUserListing,
    waitlists,
    joinSearch,
    cancelSearch,
    refetch: fetchListings,
  };
};
